import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "@/hooks/useForm";
import { globalSettingsSchema } from "@shared/validation/composites/global-settings-fields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ErrorState, Text } from "@/components/ui/text";
import { FormGrid } from "@/components/ui/form-grid";
import { FormLabel } from "@/components/ui/form-label";
import { Icon } from "@/components/ui/icons";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { PageHeader } from "@/components/ui/page-header";
import { PageTitle } from "@/contexts/page-title-context";
import { CustomSelect } from "@/components/ui/custom-select";
import { Skeleton } from "@/components/ui/skeleton";
import { HStack } from "@/components/ui/h-stack";
import { VStack } from "@/components/ui/v-stack";
import type { AdminGlobalSettings } from "@/lib/api/admin/global-settings";
import {
  getAdminGlobalSettings,
  updateAdminGlobalSettings,
} from "@/lib/api/admin/global-settings";

function NumberField({
  label,
  helper,
  value,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  helper?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <VStack gap="xs">
      <FormLabel>{label}</FormLabel>
      <InputWrapper
        type="number"
        value={String(value)}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="md:w-auto md:max-w-[260px]"
        data-testid={testId}
      />
      {helper ? (
        <Text size="xs" color="muted">
          {helper}
        </Text>
      ) : null}
    </VStack>
  );
}

function SettingsCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="bordered" padding="none">
      <CardHeader>
        <Text weight="medium">{title}</Text>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SettingsSkeleton() {
  return (
    <VStack gap="xl">
      <Card variant="bordered" padding="none">
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-36" />
        </CardContent>
      </Card>
      <Card variant="bordered" padding="none">
        <CardHeader>
          <Skeleton className="h-5 w-28" />
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VStack gap="sm">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
          </VStack>
          <VStack gap="sm">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full" />
          </VStack>
        </CardContent>
      </Card>
      <Card variant="bordered" padding="none">
        <CardHeader>
          <Skeleton className="h-5 w-24" />
        </CardHeader>
        <CardContent className="space-y-6">
          <VStack gap="sm">
            <Skeleton className="h-4 w-24" />
            <FormGrid>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
            </FormGrid>
          </VStack>
          <VStack gap="sm">
            <Skeleton className="h-4 w-24" />
            <FormGrid>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
            </FormGrid>
          </VStack>
          <VStack gap="sm">
            <Skeleton className="h-4 w-24" />
            <FormGrid>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
              <VStack gap="sm">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </VStack>
            </FormGrid>
          </VStack>
        </CardContent>
      </Card>
      <Card variant="bordered" padding="none">
        <CardHeader>
          <Skeleton className="h-5 w-36" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <FormGrid>
            <VStack gap="sm">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </VStack>
            <VStack gap="sm">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </VStack>
            <VStack gap="sm">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </VStack>
          </FormGrid>
        </CardContent>
      </Card>
    </VStack>
  );
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);

    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return prefersReducedMotion;
}

function RevealBlock({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <VStack
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transitionProperty: "opacity, transform",
        transitionDuration: prefersReducedMotion ? "120ms" : "280ms",
        transitionTimingFunction: "ease-out",
        transitionDelay: prefersReducedMotion ? "0ms" : `${index * 70}ms`,
      }}
    >
      {children}
    </VStack>
  );
}

const EMPTY_SETTINGS: AdminGlobalSettings = {
  listing_expiration_days: 0,
  max_offer_per_truck_capacity: 0,
  load_offer_expiration_hours: 0,
  tonnage_limits: { min: 0, max: 0 },
  tonnage_step: 0,
  volume_limits: { min: 0, max: 0 },
  volume_step: 0,
  temperature_limits: { min: 0, max: 0 },
  temperature_step: 0,
  price_limits: { min_per_km: 0, max_per_km: 0 },
  price_step: 0,
};

function AdminGlobalSettingsPage() {
  const { t: tAdminGlobalSettings } = useTranslation("admin.globalSettings");
  const { t: tCommon } = useTranslation("common");
  const [success, setSuccess] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ["adminGlobalSettings"],
    queryFn: getAdminGlobalSettings,
  });

  const form = useForm<AdminGlobalSettings>({
    schema: globalSettingsSchema(),
    defaults: EMPTY_SETTINGS,
    onSubmit: async (data) => {
      const updated = await updateAdminGlobalSettings(data);
      form.reset(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !form.isDirty) {
      form.reset(settingsQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!form.isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [form.isDirty]);

  const settings = settingsQuery.data ? form.fields : null;
  const loading = settingsQuery.isLoading;

  const canSave = settings !== null && !loading && form.isDirty;

  async function handleSave() {
    await form.submitSafe();
  }

  const setField = <K extends keyof AdminGlobalSettings>(
    key: K,
    value: AdminGlobalSettings[K],
  ) => {
    form.setField(key, value);
  };

  return (
    <VStack gap="xl" data-testid="admin-global-settings-page">
      <PageHeader>
        <PageTitle>
          {tAdminGlobalSettings("title") + (form.isDirty ? " *" : "")}
        </PageTitle>
        <HStack fill className="flex-1" />
        <Button
          type="button"
          disabled={!canSave || form.isSubmitting}
          loading={form.isSubmitting}
          onClick={() => void handleSave()}
        >
          {tAdminGlobalSettings("saveChanges")}
        </Button>
      </PageHeader>

      {settingsQuery.isError ? (
        <ErrorState
          title={tAdminGlobalSettings("failedToLoad")}
          action={
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void settingsQuery.refetch()}
            >
              {tCommon("retry")}
            </Button>
          }
          className="py-10"
        />
      ) : null}

      {form.serverErrors._form || form.status === "error" ? (
        <ErrorState
          title={tAdminGlobalSettings("failedToSave")}
          description={form.serverErrors._form}
          data-testid="admin-global-settings-save-error"
          action={
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void settingsQuery.refetch()}
            >
              {tCommon("retry")}
            </Button>
          }
          className="py-10"
        />
      ) : null}

      {success ? (
        <Card variant="bordered" padding="md">
          <HStack gap="lg">
            <Icon.Check color="success" />
            <Text weight="medium">{tAdminGlobalSettings("saved")}</Text>
          </HStack>
        </Card>
      ) : null}

      {loading || !settings ? (
        <SettingsSkeleton />
      ) : (
        <VStack gap="xl">
          <RevealBlock index={0}>
            <SettingsCard
              title={tAdminGlobalSettings("sections.listingSettings")}
            >
              <NumberField
                label={tAdminGlobalSettings("fields.listingExpirationDays")}
                helper={tAdminGlobalSettings("helpers.listingExpirationDays")}
                value={settings.listing_expiration_days}
                onChange={(value) => setField("listing_expiration_days", value)}
                testId="admin-global-settings-listing-expiration-days"
              />
            </SettingsCard>
          </RevealBlock>

          <RevealBlock index={1}>
            <SettingsCard
              title={tAdminGlobalSettings("sections.offerSettings")}
            >
              <FormGrid cols="md-3" gap="md">
                <NumberField
                  label={tAdminGlobalSettings("fields.maxOffersPerTruck")}
                  helper={tAdminGlobalSettings("helpers.maxOffersPerTruck")}
                  value={settings.max_offer_per_truck_capacity}
                  onChange={(value) =>
                    setField("max_offer_per_truck_capacity", value)
                  }
                  testId="admin-global-settings-max-offers-per-truck"
                />
                <NumberField
                  label={tAdminGlobalSettings(
                    "fields.loadOfferExpirationHours",
                  )}
                  helper={tAdminGlobalSettings(
                    "helpers.loadOfferExpirationHours",
                  )}
                  value={settings.load_offer_expiration_hours}
                  onChange={(value) =>
                    setField("load_offer_expiration_hours", value)
                  }
                  testId="admin-global-settings-load-offer-expiration-hours"
                />
              </FormGrid>
            </SettingsCard>
          </RevealBlock>

          <RevealBlock index={2}>
            <SettingsCard
              title={tAdminGlobalSettings("sections.routingSettings")}
            >
              <VStack gap="xs">
                <CustomSelect
                  value={settings.default_routing_provider ?? "Google"}
                  onChange={(value) =>
                    setField("default_routing_provider", value)
                  }
                  className="md:max-w-[260px]"
                  data-testid="admin-global-settings-default-routing-provider"
                  label={tAdminGlobalSettings("fields.defaultRoutingProvider")}
                  labelPosition="above"
                  options={[
                    { value: "Google", label: "Google" },
                    { value: "OpenRouteService", label: "OpenRouteService" },
                    { value: "TomTom", label: "TomTom" },
                    { value: "Mapbox", label: "Mapbox" },
                  ]}
                />
                <Text size="xs" color="muted">
                  {tAdminGlobalSettings("helpers.defaultRoutingProvider")}
                </Text>
              </VStack>
            </SettingsCard>
          </RevealBlock>

          <RevealBlock index={3}>
            <SettingsCard title={tAdminGlobalSettings("sections.limits")}>
              <VStack gap="xl">
                <VStack gap="sm">
                  <Text size="xs" color="muted">
                    {tAdminGlobalSettings("sections.weightLimits")}
                  </Text>
                  <FormGrid cols="md-3" gap="md">
                    <NumberField
                      label={tAdminGlobalSettings("fields.minWeightKg")}
                      value={settings.tonnage_limits.min}
                      onChange={(value) =>
                        setField("tonnage_limits", {
                          ...settings.tonnage_limits,
                          min: value,
                        })
                      }
                      testId="admin-global-settings-weight-min"
                    />
                    <NumberField
                      label={tAdminGlobalSettings("fields.maxWeightKg")}
                      value={settings.tonnage_limits.max}
                      onChange={(value) =>
                        setField("tonnage_limits", {
                          ...settings.tonnage_limits,
                          max: value,
                        })
                      }
                      testId="admin-global-settings-weight-max"
                    />
                  </FormGrid>
                </VStack>

                <VStack gap="sm">
                  <Text size="xs" color="muted">
                    {tAdminGlobalSettings("sections.volumeLimits")}
                  </Text>
                  <FormGrid cols="md-3" gap="md">
                    <NumberField
                      label={tAdminGlobalSettings("fields.minVolume")}
                      value={settings.volume_limits.min}
                      onChange={(value) =>
                        setField("volume_limits", {
                          ...settings.volume_limits,
                          min: value,
                        })
                      }
                      testId="admin-global-settings-volume-min"
                    />
                    <NumberField
                      label={tAdminGlobalSettings("fields.maxVolume")}
                      value={settings.volume_limits.max}
                      onChange={(value) =>
                        setField("volume_limits", {
                          ...settings.volume_limits,
                          max: value,
                        })
                      }
                      testId="admin-global-settings-volume-max"
                    />
                  </FormGrid>
                </VStack>

                <VStack gap="sm">
                  <Text size="xs" color="muted">
                    {tAdminGlobalSettings("sections.pricingLimits")}
                  </Text>
                  <FormGrid cols="md-3" gap="md">
                    <NumberField
                      label={tAdminGlobalSettings("fields.minPricePerKm")}
                      value={settings.price_limits.min_per_km}
                      onChange={(value) =>
                        setField("price_limits", {
                          ...settings.price_limits,
                          min_per_km: value,
                        })
                      }
                      testId="admin-global-settings-price-per-km-min"
                    />
                    <NumberField
                      label={tAdminGlobalSettings("fields.maxPricePerKm")}
                      value={settings.price_limits.max_per_km}
                      onChange={(value) =>
                        setField("price_limits", {
                          ...settings.price_limits,
                          max_per_km: value,
                        })
                      }
                      testId="admin-global-settings-price-per-km-max"
                    />
                  </FormGrid>
                </VStack>
              </VStack>
            </SettingsCard>
          </RevealBlock>

          <RevealBlock index={4}>
            <SettingsCard
              title={tAdminGlobalSettings("sections.notAvailableYet")}
            >
              <HStack gap="sm" className="mb-3">
                <Badge color="warning">
                  <Icon.Info size="sm" />
                  {tAdminGlobalSettings("sections.notAvailableYet")}
                </Badge>
              </HStack>
              <Text color="muted" className="mb-4">
                {tAdminGlobalSettings("helpers.notAvailableDesc")}
              </Text>
              <FormGrid cols="md-3" gap="md">
                <NumberField
                  label={tAdminGlobalSettings("fields.minDistanceKm")}
                  value={0}
                  disabled
                  onChange={() => {}}
                />
                <NumberField
                  label={tAdminGlobalSettings("fields.maxDistanceKm")}
                  value={0}
                  disabled
                  onChange={() => {}}
                />
                <NumberField
                  label={tAdminGlobalSettings(
                    "fields.platformCommissionPercent",
                  )}
                  value={0}
                  disabled
                  onChange={() => {}}
                />
              </FormGrid>
            </SettingsCard>
          </RevealBlock>
        </VStack>
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/global-settings")({
  component: AdminGlobalSettingsPage,
});
